/**
 * Banking Feature Constants
 * All hard-coded values, configuration options, and enums for the banking feature
 */

// ============================================================================
// BANKING STATUS OPTIONS
// ============================================================================

/**
 * Banking verification status options
 * Use translation keys for labels: t(`banking.status.${value}`)
 */
export const BANKING_STATUS_OPTIONS = [
  { value: "pending", label: "banking.status.pending" },
  { value: "verified", label: "banking.status.verified" },
  { value: "rejected", label: "banking.status.rejected" },
];

/**
 * Status colors for banking verification badges
 */
export const BANKING_STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  verified: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

// ============================================================================
// BANK DOCUMENT TYPES
// ============================================================================

/**
 * Required banking document types
 * Use translation keys for labels: t(`banking.documentType.${value}`)
 */
export const BANK_DOCUMENT_TYPES = {
  RIB: "rib", // Relevé d'Identité Bancaire
  CNI: "cni", // Carte Nationale d'Identité
  BANK_STATEMENT: "bank_statement",
  AUTHORIZATION: "authorization",
};

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * Banking form field names
 */
export const BANKING_FORM_FIELDS = {
  BANK_NAME: "bankName",
  ACCOUNT_NUMBER: "accountNumber",
  ACCOUNT_HOLDER: "accountHolder",
  IBAN: "iban",
  SWIFT_CODE: "swiftCode",
  BRANCH: "branch",
  RIB_DOCUMENT: "ribDocument",
  CNI_DOCUMENT: "cniDocument",
  NOTES: "notes",
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Banking validation constants
 */
export const BANKING_VALIDATION = {
  ACCOUNT_NUMBER_MIN_LENGTH: 10,
  ACCOUNT_NUMBER_MAX_LENGTH: 34,
  IBAN_MIN_LENGTH: 15,
  IBAN_MAX_LENGTH: 34,
  SWIFT_CODE_LENGTH: 11,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_FILE_TYPES: "*/*", // Accept all file types
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for banking operations
 */
// Toast Message Keys
export const BANKING_TOAST_KEYS = {
  INFO_SAVED: "banking.infoSaved",
  INFO_UPDATED: "banking.infoUpdated",
  VERIFICATION_REQUESTED: "banking.verificationRequested",
  APPROVED: "banking.approved",
  REJECTED: "banking.rejected",
  ERROR: "banking.error",
  FILE_TOO_LARGE: "banking.fileTooLarge",
  INVALID_FILE_TYPE: "banking.invalidFileType",
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Banking-related route paths
 */
export const BANKING_ROUTES = {
  SETUP: "/banking/setup",
  PENDING_REQUESTS: "/banking/pending-requests",
  MY_BANKING: "/banking/my-info",
  VERIFY: "/banking/verify/:id",
};

// ============================================================================
// PLACEHOLDERS
// ============================================================================

/**
 * Placeholder text for banking form fields (translation keys)
 */
export const BANKING_PLACEHOLDERS = {
  BANK_NAME: "banking.form.bankNamePlaceholder",
  ACCOUNT_NUMBER: "banking.form.accountNumberPlaceholder",
  ACCOUNT_HOLDER: "banking.form.accountHolderPlaceholder",
  IBAN: "banking.form.ibanPlaceholder",
  SWIFT_CODE: "banking.form.swiftCodePlaceholder",
  BRANCH: "banking.form.branchPlaceholder",
  NOTES: "banking.form.notesPlaceholder",
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for banking-related queries
 */
export const BANKING_QUERY_KEYS = {
  BASE: "banking",
  DETAIL: "banking-detail",
  PENDING: "pending-banking-requests",
  MY_BANKING: "my-banking-info",
  VERIFICATION_STATUS: "banking-verification-status",
};

// ============================================================================
// ERROR MESSAGE KEYS
// ============================================================================

/**
 * Error message translation keys for banking operations
 */
export const BANKING_ERROR_KEYS = {
  INVALID_ACCOUNT: "banking.error.invalidAccount",
  INVALID_IBAN: "banking.error.invalidIban",
  INVALID_DOCUMENT: "banking.error.invalidDocument",
  FILE_TOO_LARGE: "banking.error.fileTooLarge",
  UNSUPPORTED_FILE_TYPE: "banking.error.unsupportedFileType",
  MISSING_REQUIRED_DOCUMENTS: "banking.error.missingRequiredDocuments",
};

// ============================================================================
// UI TEXT KEYS
// ============================================================================

/**
 * UI text translation keys for banking feature
 */
export const BANKING_UI_TEXT = {
  PENDING_VERIFICATION: "banking.pendingVerification",
  VERIFIED: "banking.verified",
  REJECTED: "banking.rejected",
  SUBMIT_FOR_VERIFICATION: "banking.submitForVerification",
  APPROVE_REQUEST: "banking.approveRequest",
  REJECT_REQUEST: "banking.rejectRequest",
  VIEW_DOCUMENTS: "banking.viewDocuments",
  UPLOAD_DOCUMENTS: "banking.uploadDocuments",
};
